import { Capacitor } from '@capacitor/core';
import { App as AppCap } from '@capacitor/app';
import { Device } from '@capacitor/device';
import moment from './moment';
import Basket from './basket';
import packageInfo from '../../package.json';

/*
 * Diagnostic snapshot sent alongside a feedback submission so support can tell
 * which device, build and account a report came from.
 * Deliberately a named set of fields - never the whole profile, no auth token
 * and no saved card details.
 */

const getDeviceInfo = async () => {
  try {
    return await Device.getInfo();
  } catch (e) {
    return {};
  }
};

const getDeviceValue = async (fn) => {
  try {
    const result = await fn();
    return result?.value ?? null;
  } catch (e) {
    return null;
  }
};

/*
 * The installed app version is not the bundle version: live updates are
 * configurable per build (CapacitorUpdater in capacitor.config.ts), so a device
 * can be running a newer bundle than the build it installed from the store.
 * The first number of the installed version is also the client id (see lib/api.js).
 */
const getInstalledAppInfo = async () => {
  if (!Capacitor.isNativePlatform()) {
    return { version: null, clientId: null };
  }
  try {
    const info = await AppCap.getInfo();
    return { version: info.version, clientId: parseInt(info.version.split('.')[0]) };
  } catch (e) {
    return { version: null, clientId: null };
  }
};

const getStampBalance = (profile) => {
  if (!Array.isArray(profile.stamp_cards_json)) {
    return null;
  }
  return profile.stamp_cards_json.reduce((sum, item) => sum + (item.stamp_balance || 0), 0);
};

export const getFeedbackMetadata = async (profile = {}) => {
  const [deviceInfo, languageCode, languageTag, installedApp] = await Promise.all([
    getDeviceInfo(),
    getDeviceValue(Device.getLanguageCode),
    getDeviceValue(Device.getLanguageTag),
    getInstalledAppInfo(),
  ]);
  const now = moment();
  return {
    device: {
      platform: Capacitor.getPlatform(),
      is_native: Capacitor.isNativePlatform(),
      model: deviceInfo.model ?? null,
      manufacturer: deviceInfo.manufacturer ?? null,
      operating_system: deviceInfo.operatingSystem ?? null,
      os_version: deviceInfo.osVersion ?? null,
      is_simulator: deviceInfo.isVirtual ?? null,
      language: languageCode,
      locale: languageTag,
    },
    app: {
      installed_version: installedApp.version,
      bundle_version: packageInfo.version,
    },
    time: {
      timezone: moment.tz.guess(),
      device_datetime: now.format(),
      utc_offset_minutes: now.utcOffset(),
    },
    user: {
      user_id: profile.id ?? null,
      client_id: installedApp.clientId,
      email: profile.email ?? null,
      is_guest: profile.is_guest ?? null,
      favourite_location: profile.favourite_location ?? null,
      available_balance: profile.available_balance ?? null,
      milestone_balance: profile.milestone_balance ?? null,
      stamp_balance: getStampBalance(profile),
      order_type: Basket.order_type || null,
    },
  };
};

export default getFeedbackMetadata;
